import { useMemo } from "react";
import { useSelector } from "react-redux";
import { TextStyle, TextMetrics } from "pixi.js";
import { useDimensions } from "../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../pixiSettings";
import { PixiSettings } from "../pixiSettings";
import { FONT_CONFIG } from "../pixiSettings";
import { formatToCustomLocale } from "../utilities";
import { selectCurrency, selectBets } from "../State/selectors";

// Toolbar layout constraints
const MAX_TOOLBAR_WIDTH = 1920;
const MAX_TOOLBAR_WIDTH_MOBILE_LANDSCAPE_MULTIPLIER = 10;

// Max bet button configuration - matches ButtonMaxBet component (moved outside to prevent recreation)
const MAX_BET_BUTTON_CONFIG = {
  BASE_WIDTH: 80,
  BASE_HEIGHT: 80,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.97,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.75,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.3,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.1,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

const MAX_BET_BUTTON_CONFIG_DESKTOP = {
  BASE_WIDTH: 93,
  BASE_HEIGHT: 64,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 0.9,
    },
  },
};

// Per-tier height multipliers (applied to buttonConfig.height)
const TOOLBAR_HEIGHT_MULTIPLIERS = {
  MOBILE_PORTRAIT: 0,
  MOBILE_LANDSCAPE: 1.35,
  MOBILE_SMALL_LANDSCAPE: 1.5,
  TABLET_PORTRAIT: 0,
  TABLET_LANDSCAPE: 1.85,
  DESKTOP: 2.18,
};

// Visual height multipliers for portrait toolbar background (doesn't affect layout)
const TOOLBAR_VISUAL_HEIGHT_MULTIPLIERS = {
  MOBILE_SMALL_PORTRAIT: 2.2,
  MOBILE_PORTRAIT: 1.575,
  TABLET_PORTRAIT: 2.0,
};

/**
 * Hook for calculating Toolbar dimensions with width capping
 * - Mobile landscape: capped dynamically at uiSymbolSize * MAX_TOOLBAR_WIDTH_MOBILE_LANDSCAPE_MULTIPLIER
 * - All other cases: capped at MAX_TOOLBAR_WIDTH (1920px)
 * Simpler alternative to useToolbarDimensions for the bottom toolbar
 * Returns toolbar object with width, height, and layout positions based on device/orientation
 */
export const useSecondaryToolbar = () => {
  const {
    dimensions,
    device,
    orientation,
    getResponsiveConfig,
    uiSymbolSize,
    uiSymbolScale,
    gameWidth,
    isMobileSmall,
  } = useDimensions();
  const currency = useSelector(selectCurrency);
  const bets = useSelector(selectBets);

  // Memoize font multiplier calculation (used by both balance and bet width calculations)
  const fontMultiplier = useMemo(() => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      return orientation === PixiSettings.ORIENTATION.PORTRAIT ? 1.0 : 1.1;
    } else if (device === PixiSettings.DEVICE.TABLET) {
      return orientation === PixiSettings.ORIENTATION.PORTRAIT ? 1.65 : 1.25;
    } else {
      return 1.0; // Desktop
    }
  }, [device, orientation]);

  // Memoize button config
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Memoize max width calculation
  const maxWidth = useMemo(() => {
    if (
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.LANDSCAPE
    ) {
      return uiSymbolSize * MAX_TOOLBAR_WIDTH_MOBILE_LANDSCAPE_MULTIPLIER;
    }
    // Desktop: derive from gameWidth (dynamic, tracks symbolScale)
    // Spine art extends ~20% beyond the game grid area
    if (device === PixiSettings.DEVICE.DESKTOP && gameWidth) {
      return Math.round(gameWidth * 1.2);
    }
    return MAX_TOOLBAR_WIDTH;
  }, [device, orientation, uiSymbolSize, gameWidth]);

  // Memoize width calculation
  const width = useMemo(
    () => Math.min(dimensions.width, maxWidth),
    [dimensions.width, maxWidth],
  );

  const height = useMemo(() => {
    if (device === PixiSettings.DEVICE.MOBILE && isMobileSmall) {
      return orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? buttonConfig.height * TOOLBAR_HEIGHT_MULTIPLIERS.MOBILE_PORTRAIT
        : buttonConfig.height *
            TOOLBAR_HEIGHT_MULTIPLIERS.MOBILE_SMALL_LANDSCAPE;
    } else if (device === PixiSettings.DEVICE.MOBILE) {
      return orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? buttonConfig.height * TOOLBAR_HEIGHT_MULTIPLIERS.MOBILE_PORTRAIT
        : buttonConfig.height * TOOLBAR_HEIGHT_MULTIPLIERS.MOBILE_LANDSCAPE;
    } else if (device === PixiSettings.DEVICE.TABLET) {
      return orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? buttonConfig.height * TOOLBAR_HEIGHT_MULTIPLIERS.TABLET_PORTRAIT
        : buttonConfig.height * TOOLBAR_HEIGHT_MULTIPLIERS.TABLET_LANDSCAPE;
    } else {
      return buttonConfig.height * TOOLBAR_HEIGHT_MULTIPLIERS.DESKTOP;
    }
  }, [device, orientation, isMobileSmall, buttonConfig.height]);

  // Visual height for the toolbar background sprite (can differ from layout height)
  // In portrait modes, this shows the gradient background without affecting element positioning
  const visualHeight = useMemo(() => {
    if (
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.PORTRAIT
    ) {
      const multiplier = isMobileSmall
        ? TOOLBAR_VISUAL_HEIGHT_MULTIPLIERS.MOBILE_SMALL_PORTRAIT
        : TOOLBAR_VISUAL_HEIGHT_MULTIPLIERS.MOBILE_PORTRAIT;
      return buttonConfig.height * multiplier;
    }
    if (
      device === PixiSettings.DEVICE.TABLET &&
      orientation === PixiSettings.ORIENTATION.PORTRAIT
    ) {
      return (
        buttonConfig.height * TOOLBAR_VISUAL_HEIGHT_MULTIPLIERS.TABLET_PORTRAIT
      );
    }
    // Non-portrait: visual height matches layout height
    return height;
  }, [device, orientation, isMobileSmall, buttonConfig.height, height]);

  // Memoize MaxBet button width calculation
  const maxBetWidth = useMemo(() => {
    const maxBetConfig =
      device === PixiSettings.DEVICE.DESKTOP
        ? getResponsiveConfig(MAX_BET_BUTTON_CONFIG_DESKTOP, true)
        : getResponsiveConfig(MAX_BET_BUTTON_CONFIG, true);
    return maxBetConfig.width;
  }, [device, getResponsiveConfig]);

  // Memoize balance width calculation
  const balanceWidth = useMemo(() => {
    const baseFontSize = 32;
    const responsiveFontSize = baseFontSize * fontMultiplier;
    const idealFontSize = Math.round(responsiveFontSize * uiSymbolScale);

    // Calculate max balance width (7-digit balance)
    const maxBalance = 9999999;
    const maxBalanceText = `${formatToCustomLocale(maxBalance)} ${currency}`;
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "700",
    });
    const maxBalanceMetrics = TextMetrics.measureText(
      maxBalanceText,
      tempStyle,
    );

    return maxBalanceMetrics.width;
  }, [fontMultiplier, uiSymbolScale, currency]);

  // Memoize bet width calculation
  const betWidth = useMemo(() => {
    const baseFontSize = 32;
    const responsiveFontSize = baseFontSize * fontMultiplier;
    const fontSize = Math.round(responsiveFontSize * uiSymbolScale);

    // Calculate max bet width based on largest bet in bets array
    if (!bets || bets.length === 0) {
      return 100; // Fallback width
    }

    const maxBet = Math.max(...bets);
    const maxBetText = `${formatToCustomLocale(maxBet)} ${currency}`;
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: fontSize,
      fontWeight: "700",
    });
    const maxBetMetrics = TextMetrics.measureText(maxBetText, tempStyle);

    return maxBetMetrics.width;
  }, [fontMultiplier, uiSymbolScale, bets, currency]);

  return useMemo(() => {
    // Calculate positions based on device/orientation
    const calculatePositions = () => {
      // Device and orientation specific gap configurations (only for sequential layouts)
      let gap; // Gap between Balance and Bet (or Balance and MaxBet on tablet landscape)
      let maxBetGap; // Gap between Bet and MaxBet

      if (
        device === PixiSettings.DEVICE.MOBILE &&
        orientation === PixiSettings.ORIENTATION.LANDSCAPE
      ) {
        gap = buttonConfig.width * 2.0; // Mobile Landscape gap
        maxBetGap = buttonConfig.width * 2.0; // Mobile Landscape MaxBet gap
      } else if (
        device === PixiSettings.DEVICE.TABLET &&
        orientation === PixiSettings.ORIENTATION.LANDSCAPE
      ) {
        gap = buttonConfig.width * 1.1; // Tablet Landscape gap (Balance to MaxBet)
        maxBetGap = buttonConfig.width * 2.7; // Tablet Landscape MaxBet gap (MaxBet to Bet)
      } else if (device === PixiSettings.DEVICE.DESKTOP) {
        gap = buttonConfig.width * 4.0; // Desktop gap (Balance to Bet)
        maxBetGap = buttonConfig.width * 2.0; // Desktop MaxBet gap (Bet to MaxBet)
      }

      if (device === PixiSettings.DEVICE.MOBILE) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          // Portrait: Balance on left, Bet on right (keep separate positioning)
          // MaxBet uses original positioning logic from ButtonMaxBet component
          return {
            balance: {
              x: buttonConfig.width * 0.225,
              width: balanceWidth,
            },
            bet: {
              x: width - buttonConfig.width * 0.225,
              width: betWidth,
            },
          };
        } else {
          // Landscape: Balance -> Bet (left to right)
          // MaxBet uses original positioning logic from ButtonMaxBet component
          const balanceX = buttonConfig.width * 1.5;
          const betX = balanceX + balanceWidth / 2 + gap + betWidth / 2;
          return {
            balance: {
              x: balanceX,
              width: balanceWidth,
            },
            bet: {
              x: betX,
              width: betWidth,
            },
          };
        }
      } else if (device === PixiSettings.DEVICE.TABLET) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          // Portrait: Balance on left, Bet on right (keep separate positioning)
          // MaxBet uses original positioning logic from ButtonMaxBet component
          return {
            balance: {
              x: buttonConfig.width * 0.75,
              width: balanceWidth,
            },
            bet: {
              x: width - buttonConfig.width * 0.75,
              width: betWidth,
            },
          };
        } else {
          // Landscape: Balance -> MaxBet -> Bet (left to right)
          // MaxBet goes between Balance and Bet on tablet landscape
          // Account for center-anchored components
          const balanceX = buttonConfig.width * 0.5;
          const maxBetX = balanceX + balanceWidth / 2 + gap + maxBetWidth / 2;
          const betX = maxBetX + maxBetWidth / 2 + gap + betWidth / 2;
          return {
            balance: {
              x: balanceX,
              width: balanceWidth,
            },
            bet: {
              x: betX,
              width: betWidth,
            },
            maxBet: {
              x: maxBetX,
              width: maxBetWidth,
            },
          };
        }
      } else {
        // Desktop: Balance -> Bet -> MaxBet (left to right)
        // Account for center-anchored components
        // Use different gaps: 'gap' between Balance and Bet, 'maxBetGap' between Bet and MaxBet
        const balanceX = buttonConfig.width * 2.5;
        const betX = balanceX + balanceWidth / 2 + gap + betWidth / 2;
        const maxBetX = betX + betWidth / 2 + maxBetGap + maxBetWidth / 2;
        return {
          balance: {
            x: balanceX,
            width: balanceWidth,
          },
          bet: {
            x: betX,
            width: betWidth,
          },
          maxBet: {
            x: maxBetX,
            width: maxBetWidth,
          },
        };
      }
    };

    const layout = calculatePositions();

    return { width, height, visualHeight, layout };
  }, [
    width,
    height,
    visualHeight,
    balanceWidth,
    betWidth,
    maxBetWidth,
    buttonConfig,
    device,
    orientation,
  ]);
};
